export const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3.x/dist/tabler-icons.min.css');

  :root {
    --blue:       #0a6eb4;
    --blue-dark:  #084f82;
    --blue-light: #e6f1fb;
    --blue-mid:   #b5d4f4;
    --navy:       #042C53;
    --bg:         #f8fafc;
    --surface:    #ffffff;
    --border:     rgba(0,0,0,0.08);
    --border-md:  rgba(0,0,0,0.12);
    --text:       #0f172a;
    --muted:      #64748b;
    --hint:       #94a3b8;
    --green:      #0f6e56;
    --green-bg:   #e1f5ee;
    --amber:      #854f0b;
    --amber-bg:   #faeeda;
    --red:        #a32d2d;
    --red-bg:     #fcebeb;
    --purple:     #534ab7;
    --purple-bg:  #eeedfe;
    --shadow-sm:  0 1px 3px rgba(0,0,0,0.06);
    --shadow:     0 2px 8px rgba(0,0,0,0.08);
    --radius:     8px;
    --radius-lg:  12px;
  }

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    -webkit-tap-highlight-color: transparent;
  }

  html { height: -webkit-fill-available; scroll-behavior: smooth; }

  body {
    font-family: 'Inter', -apple-system, sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    min-height: -webkit-fill-available;
    overscroll-behavior: none;
    -webkit-font-smoothing: antialiased;
    font-size: 14px;
    line-height: 1.5;
  }

  input, select, textarea, button { font-family: inherit; font-size: 14px; }
  input[type="date"] { font-size: 14px; }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border-md); border-radius: 4px; }

  .table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .safe-bottom { padding-bottom: env(safe-area-inset-bottom, 16px); }

  @media (max-width: 640px) {
    .hide-mobile   { display: none !important; }
    .stack-mobile  { flex-direction: column !important; }
    .full-mobile   { width: 100% !important; }
    .static-mobile { position: static !important; }
  }
`;

// Shared style objects used across pages
export const S = {
  // Page wrapper
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    fontFamily: 'Inter, sans-serif',
  },

  // Top header bar
  header: {
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    padding: '0 20px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    boxShadow: 'var(--shadow-sm)',
  },

  // Page content area
  content: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '20px 16px 80px',
  },

  // Card
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
  },

  // Card header
  cardHeader: {
    padding: '14px 18px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  // Input
  input: {
    width: '100%',
    background: 'var(--surface)',
    border: '1px solid var(--border-md)',
    borderRadius: 'var(--radius)',
    padding: '8px 12px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    transition: 'border-color .15s',
  },

  // Primary button
  btnPrimary: {
    background: 'var(--blue)',
    border: 'none',
    borderRadius: 'var(--radius)',
    padding: '8px 16px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },

  // Secondary button
  btnSecondary: {
    background: 'transparent',
    border: '1px solid var(--border-md)',
    borderRadius: 'var(--radius)',
    padding: '8px 14px',
    color: 'var(--muted)',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },

  // Label
  label: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '.5px',
    color: 'var(--muted)',
    marginBottom: '5px',
  },

  // Table header cell
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '.5px',
    color: 'var(--muted)',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
    background: 'var(--bg)',
  },

  // Table data cell
  td: {
    padding: '11px 14px',
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'middle',
    fontSize: '13px',
  },

  // Status badges
  badge: (color, bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    background: bg,
    color: color,
    fontSize: '11px',
    fontWeight: '500',
    padding: '2px 8px',
    borderRadius: '20px',
    whiteSpace: 'nowrap',
  }),

  // Back button
  backBtn: {
    background: 'transparent',
    border: '1px solid var(--border-md)',
    borderRadius: 'var(--radius)',
    padding: '6px 12px',
    color: 'var(--muted)',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },

  // Mono text (stock#, VIN, PO)
  mono: {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '13px',
    fontWeight: '500',
  },
};
